'use client';

import { useCallback, useRef, type MouseEvent } from 'react';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import {
  RING_PRO_US_STATS_DISCLAIMER_ID,
  scrollToRingProUsStatsDisclaimer,
} from 'utils/ringProUsBatteryDisclaimer';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useGsapContext } from 'hooks/animation/useGsapContext';
import { useIsomorphicLayoutEffect } from 'hooks/dom/useIsomorphicLayoutEffect';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import { homeAssetSrc } from './homeAssetSrc';
import { bindOnceBeat } from './scrollBeat';
import v3 from './v3.module.css';
import styles from './BatteryLifeSection.module.css';

const PINNED_MEDIA_QUERY = '(prefers-reduced-motion: no-preference)';
const DESKTOP_LAYOUT_QUERY = '(min-width: 992px)';

/** Wipe shape: opaque up to progress·100%, fading out until
 *  (progress / ARC_OPAQUE_END)·100%. Lower = longer fade tail. */
const ARC_OPAQUE_END = 0.4;
const DAYS_MAX = 15;

const RING_DESKTOP_SRC = homeAssetSrc('web_v2/home-v4/battery/ring-full.png');
const RING_MOBILE_SRC = homeAssetSrc('web_v2/home-v4/battery/ring-min.png');
const ARC_SVG_SRC = getCompressedAssetUrl(
  '/web_v2/site-static/battery/arc-intersect.svg',
);

/** Orbiting radial shine overlay — base fill comes from the Figma SVG. */
function paintShine(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  t: number,
) {
  ctx.clearRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'lighter';
  const gu = ((t / 8) % 1.5) - 0.25;
  const cu = Math.min(1, Math.max(0, gu));
  const gy = h * (0.12 + (0.66 - 0.12) * (2 * cu - 1) ** 2);
  const glow = ctx.createRadialGradient(w * gu, gy, 0, w * gu, gy, h * 1.5);
  glow.addColorStop(0, 'rgba(214, 255, 196, 0.32)');
  glow.addColorStop(1, 'rgba(214, 255, 196, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
}

export const BatteryLifeSection = ({
  'aria-label': ariaLabel,
}: {
  'aria-label'?: string;
}) => {
  const t = useTranslations('homepage');
  const { region } = useRegionLocale();

  const outerRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const uptoLabelRef = useRef<HTMLParagraphElement | null>(null);
  const batteryLabelRef = useRef<HTMLParagraphElement | null>(null);
  const statRef = useRef<HTMLParagraphElement | null>(null);
  const counterRef = useRef<HTMLSpanElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const arcWipeRef = useRef<HTMLDivElement | null>(null);

  const gsapCtx = useGsapContext(outerRef);

  useIsomorphicLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return undefined;
    }

    const draw = (time: number) => {
      const cssW = canvas.clientWidth;
      const cssH = canvas.clientHeight;
      if (cssW < 1 || cssH < 1) return;
      const dpr = window.matchMedia('(pointer: coarse)').matches
        ? 1
        : window.devicePixelRatio || 1;
      const pixelW = Math.round(cssW * dpr);
      const pixelH = Math.round(cssH * dpr);
      if (canvas.width !== pixelW) canvas.width = pixelW;
      if (canvas.height !== pixelH) canvas.height = pixelH;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      paintShine(ctx, cssW, cssH, time);
    };

    let raf = 0;
    let running = false;
    const loop = (now: number) => {
      draw(now / 1000);
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (running) return;
      running = true;
      raf = requestAnimationFrame(loop);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(raf);
    };
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) start();
      else stop();
    });
    io.observe(canvas);
    return () => {
      stop();
      io.disconnect();
    };
  }, []);

  useIsomorphicLayoutEffect(() => {
    gsapCtx.add(() => {
      const mm = gsap.matchMedia();

      mm.add(PINNED_MEDIA_QUERY, () => {
        const outer = outerRef.current;
        const title = titleRef.current;
        const uptoLabel = uptoLabelRef.current;
        const batteryLabel = batteryLabelRef.current;
        const stat = statRef.current;
        const counter = counterRef.current;
        const arcWipe = arcWipeRef.current;
        if (
          !outer ||
          !title ||
          !uptoLabel ||
          !batteryLabel ||
          !stat ||
          !counter ||
          !arcWipe
        ) {
          return;
        }

        const setArcProgress = (p: number) => {
          const clamped = Math.max(0, Math.min(1, p));
          const solid = (clamped * 100).toFixed(2);
          const edge = ((clamped / ARC_OPAQUE_END) * 100).toFixed(2);
          const mask = `linear-gradient(90deg, #000 ${solid}%, rgba(0, 0, 0, 0) ${edge}%)`;
          arcWipe.style.maskImage = mask;
          arcWipe.style.webkitMaskImage = mask;
        };

        setArcProgress(0);

        const isDesktop = () =>
          window.matchMedia(DESKTOP_LAYOUT_QUERY).matches;
        const titleOut = {
          ease: 'power1.out' as const,
          duration: 0.35,
          overwrite: 'auto' as const,
        };
        const claimIn = {
          ease: 'power1.out' as const,
          duration: 0.7,
          overwrite: 'auto' as const,
        };

        gsap.set(title, { opacity: 1, y: 0 });
        gsap.set([stat, uptoLabel, batteryLabel], { opacity: 0 });

        const tl = gsap.timeline({
          paused: true,
          onStart: () => {
            counter.textContent = '0';
          },
        });

        tl.fromTo(
          title,
          { opacity: 1, y: 0 },
          { opacity: 0, y: -24, ...titleOut },
          0,
        );

        tl.fromTo(
          stat,
          { opacity: 0, scale: 0.92 },
          { opacity: 1, scale: 1, ...claimIn },
          titleOut.duration,
        );

        tl.fromTo(
          uptoLabel,
          {
            opacity: 0,
            x: () => (isDesktop() ? 96 : 0),
            y: () => (isDesktop() ? 0 : 56),
          },
          { opacity: 1, x: 0, y: 0, ...claimIn },
          titleOut.duration,
        );

        tl.fromTo(
          batteryLabel,
          {
            opacity: 0,
            x: () => (isDesktop() ? -96 : 0),
            y: () => (isDesktop() ? 0 : -56),
          },
          { opacity: 1, x: 0, y: 0, ...claimIn },
          titleOut.duration,
        );

        const progress = { value: 0 };
        tl.fromTo(
          progress,
          { value: 0 },
          {
            value: 1,
            ease: 'power1.out',
            duration: 1.8,
            overwrite: 'auto',
            onUpdate: () => {
              setArcProgress(progress.value);
              counter.textContent = `${Math.round(progress.value * DAYS_MAX)}`;
            },
          },
          titleOut.duration,
        );

        const unbind = bindOnceBeat({
          trigger: outer,
          timeline: tl,
          start: 'top 40%',
        });

        return () => {
          unbind();
          tl.kill();
        };
      });

      return () => mm.revert();
    });
  }, [gsapCtx]);

  const titleId = 'battery-life-title';
  const isUsHome = region === 'US';
  const onBatteryFootnoteClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      scrollToRingProUsStatsDisclaimer();
    },
    [],
  );

  return (
    <section
      ref={outerRef}
      className={
        region === 'US' ? `${styles.outer} ${styles.us}` : styles.outer
      }
      aria-labelledby={titleId}
      aria-label={ariaLabel}
    >
      <div className={styles.stage}>
        <div className={styles.textStack}>
          <div ref={titleRef} className={styles.titleLayer}>
            <h2 id={titleId} className={`${v3.sectionTitle} ${styles.title}`}>
              {t('homepage.batteryLife.title')}
            </h2>
          </div>

          <div className={styles.claimLayer} aria-hidden>
            <p ref={uptoLabelRef} className={styles.sideLabel}>
              {t('homepage.batteryLife.dial.upto')}
            </p>
            <p ref={statRef} className={styles.stat}>
              <span ref={counterRef} className={styles.counter}>
                15
              </span>{' '}
              {t('homepage.batteryLife.dial.days')}
              {isUsHome ? (
                <CustomLink
                  href={`#${RING_PRO_US_STATS_DISCLAIMER_ID}`}
                  className={styles.batteryFootnote}
                  aria-label={t('homepage.ringProUs.stats.footnote.battery')}
                  onClick={onBatteryFootnoteClick}
                >
                  *
                </CustomLink>
              ) : null}
            </p>
            <p ref={batteryLabelRef} className={styles.sideLabel}>
              {t('homepage.batteryLife.dial.batteryLife')}
            </p>
          </div>

          <p className={v3.srOnly}>{t('homepage.batteryLife.srClaim')}</p>
        </div>

        <div className={styles.arcLayer} aria-hidden>
          <div ref={arcWipeRef} className={styles.arcWipeStack}>
            <img className={styles.arcSvg} src={ARC_SVG_SRC} alt="" aria-hidden />
            <canvas ref={canvasRef} className={styles.arcShine} aria-hidden />
          </div>
        </div>

        <div className={styles.ringLayer} aria-hidden>
          <div className={styles.ringHost}>
            <CustomImage
              className={`${styles.ringImg} ${styles.ringDesktop}`}
              src={RING_DESKTOP_SRC}
              alt=""
              fill
              sizes="100vw"
            />
            <CustomImage
              className={`${styles.ringImg} ${styles.ringMobile}`}
              src={RING_MOBILE_SRC}
              alt=""
              fill
              sizes="100vw"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BatteryLifeSection;
